'use client';

import React, { useMemo, useState } from 'react';
import {
  ClockCounterClockwiseIcon,
  MagnifyingGlassIcon,
  SpinnerGapIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { Combobox } from '@tranquility-ai/theme-components/combobox';
import { Input } from '@tranquility-ai/theme-components/input';
import { Modal } from '@tranquility-ai/theme-components/modal';
import { Icon, Inline, Stack, Surface, Text } from '@tranquility-ai/theme-components/primitives';
import '@tranquility-ai/theme-components/modal.css';
import { CsamSectionHeader } from '@/components/csam-section-header';
import { useCsamAuditQuery, type CsamAuditQueryParams } from '@/hooks/useCsamAuditQuery';
import { AuditApiError, type CsamAuditDimension, type CsamAuditRow } from '@/shared/cors-audit-api';
import { useAppFormatters } from '@/i18n/useAppFormatters';
import { useTimePilotT, type TranslateFn } from '@/i18n/useTimePilotT';
import './csam-audit-log.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DIMENSIONS: Array<{ key: CsamAuditDimension; labelKey: string; placeholderKey: string }> = [
  { key: 'tenant', labelKey: 'csam.audit.dimension.all', placeholderKey: '' },
  { key: 'case', labelKey: 'csam.audit.dimension.case', placeholderKey: 'csam.audit.placeholder.case' },
  { key: 'user', labelKey: 'csam.audit.dimension.user', placeholderKey: 'csam.audit.placeholder.user' },
];

/** ``YYYY-MM-DD`` for a Date, in UTC. */
function toDateInput(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function formatTimestamp(
  iso: string,
  formatDateTime: (value: string, options?: Intl.DateTimeFormatOptions) => string,
): string {
  if (!iso) return '—';
  try {
    return formatDateTime(iso, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
  } catch {
    return iso;
  }
}

function auditQueryErrorCopy(error: unknown): string {
  const status = error instanceof AuditApiError ? error.status : null;
  if (status === 403) {
    return 'You do not have permission to view the CSAM audit log.';
  }
  if (status === 503) {
    return 'The CSAM audit log is temporarily unavailable. Please try again later.';
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Audit query failed. Please try again.';
}

/** Prefer email, fall back to user id. System ingest events have no actor. */
function actorLabel(row: CsamAuditRow): string {
  if (row.variant === 'DATA_INGESTION' && !row.email && !row.user_id) return 'System';
  return row.email || row.user_id || '—';
}

// Column render order for the results table.
const COLUMNS: Array<{ key: keyof CsamAuditRow | 'actor'; labelKey: string; mono?: boolean }> = [
  { key: 'occurred_at', labelKey: 'timeline.table.column.time' },
  { key: 'action', labelKey: 'csam.audit.column.action' },
  { key: 'variant', labelKey: 'csam.audit.column.variant' },
  { key: 'actor', labelKey: 'csam.audit.column.user' },
  { key: 'source_ip', labelKey: 'csam.audit.column.sourceIp', mono: true },
  { key: 'case_id', labelKey: 'csam.audit.column.case' },
  { key: 's3_uri', labelKey: 'csam.audit.column.uri', mono: true },
  { key: 'detection_type', labelKey: 'workProducts.column.type' },
  { key: 'result', labelKey: 'csam.audit.column.result' },
];

const ACTION_LABELS: Record<string, string> = {
  FINDING_CREATED: 'Flagged as CSAM',
  OBJECT_ACCESSED: 'Media accessed',
  STATUS_CHANGED: 'Review status changed',
  INCORRECT_FLAG_REPORTED: 'Incorrect flag reported',
};

const VARIANT_KEYS: Record<string, string> = {
  ORIGINAL: 'media.original',
  OBFUSCATED: 'media.obfuscated',
  MANUAL: 'csam.audit.variant.userFlagged',
  DATA_INGESTION: 'csam.provenance.systemDetected',
};

function labelOrRaw(map: Record<string, string>, value: string): string {
  return map[value] || value || '—';
}

function variantLabel(value: string, t: TranslateFn): string {
  const key = VARIANT_KEYS[value];
  return key ? t(key) : value || '—';
}

function actionLabel(row: CsamAuditRow): string {
  if (row.action === 'FINDING_CREATED') {
    return row.variant === 'MANUAL' ? 'Flagged as CSAM' : 'System detected CSAM';
  }
  return labelOrRaw(ACTION_LABELS, row.action);
}

function cellValue(
  row: CsamAuditRow,
  key: keyof CsamAuditRow | 'actor',
  formatDateTime: (value: string, options?: Intl.DateTimeFormatOptions) => string,
  t: TranslateFn,
): string {
  if (key === 'actor') return actorLabel(row);
  if (key === 'occurred_at') return formatTimestamp(row.occurred_at, formatDateTime);
  if (key === 'action') return actionLabel(row);
  if (key === 'variant') return variantLabel(row.variant, t);
  return (row[key as string] ?? '').toString() || '—';
}

// Full field set shown in the detail modal — a superset of the table columns
// (adds event_id + the finding-status transition), rendered untruncated.
const DETAIL_FIELDS: Array<{ key: string; labelKey: string; mono?: boolean }> = [
  { key: 'occurred_at', labelKey: 'timeline.table.column.time' },
  { key: 'action', labelKey: 'csam.audit.column.action' },
  { key: 'result', labelKey: 'csam.audit.column.result' },
  { key: 'variant', labelKey: 'csam.audit.column.variant' },
  { key: 'email', labelKey: 'csam.audit.detail.userEmail' },
  { key: 'user_id', labelKey: 'csam.audit.detail.userId', mono: true },
  { key: 'source_ip', labelKey: 'csam.audit.column.sourceIp', mono: true },
  { key: 'case_id', labelKey: 'csam.audit.column.case' },
  { key: 'detection_type', labelKey: 'csam.audit.detail.detectionType' },
  { key: 'finding_status_from', labelKey: 'csam.audit.detail.statusFrom' },
  { key: 'finding_status_to', labelKey: 'csam.audit.detail.statusTo' },
  { key: 's3_uri', labelKey: 'csam.audit.column.uri', mono: true },
  { key: 'event_id', labelKey: 'csam.audit.detail.eventId', mono: true },
];

function detailValue(
  row: CsamAuditRow,
  key: string,
  formatDateTime: (value: string, options?: Intl.DateTimeFormatOptions) => string,
  t: TranslateFn,
): string {
  if (key === 'occurred_at') return formatTimestamp(row.occurred_at, formatDateTime);
  if (key === 'action') return actionLabel(row);
  if (key === 'variant') return variantLabel(row.variant, t);
  return (row[key] ?? '').toString() || '—';
}

function AuditEventDetailModal({
  row,
  onClose,
}: {
  row: CsamAuditRow | null;
  onClose: () => void;
}) {
  const t = useTimePilotT();
  const { formatDateTime } = useAppFormatters();
  return (
    <Modal
      isOpen={row != null}
      onClose={onClose}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={row ? actionLabel(row) : t('csam.audit.eventTitle')}
      size="md"
    >
      {row ? (
        <Stack gap="md">
          {DETAIL_FIELDS.map((field) => (
            <Stack key={field.key} gap="xs">
              <Text variant="bodyTiny" weight="semibold">
                {t(field.labelKey)}
              </Text>
              <Text variant="bodySmall">{detailValue(row, field.key, formatDateTime, t)}</Text>
            </Stack>
          ))}
        </Stack>
      ) : null}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * CSAM audit-log viewer. Lets an authorized reviewer query the per-tenant WORM
 * audit trail by file / user / case (or tenant-wide) over a date window. The
 * tenant and date bounds are enforced server-side; queries run async on Athena
 * (start + poll, up to ~30 s) so the "Run" button shows a spinner while polling.
 *
 * Pass ``embedded`` to render just the filter + results (no full-page wrapper or
 * page header) so the viewer can sit inside another view's chrome — e.g. as a
 * tab within the CSAM Dashboard.
 */
const CsamAuditLog: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const t = useTimePilotT();
  const { formatDateTime } = useAppFormatters();
  const today = useMemo(() => new Date(), []);
  const defaultFrom = useMemo(() => {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() - 30);
    return d;
  }, [today]);

  // Draft filters (edited freely) vs. applied filters (run on demand).
  // All is the first option, so the dropdown opens on the tenant-wide scope.
  const [dimension, setDimension] = useState<CsamAuditDimension>(DIMENSIONS[0].key);
  const [value, setValue] = useState('');
  const [fromDate, setFromDate] = useState(toDateInput(defaultFrom));
  const [toDate, setToDate] = useState(toDateInput(today));

  const [applied, setApplied] = useState<CsamAuditQueryParams | null>(null);

  // The audit event whose full detail is being viewed (null = no modal).
  const [selectedRow, setSelectedRow] = useState<CsamAuditRow | null>(null);

  const query = useCsamAuditQuery(applied ?? { dimension: 'tenant' }, {
    enabled: applied !== null,
  });

  const needsValue = dimension !== 'tenant';
  const canRun = !needsValue || value.trim().length > 0;

  const handleRun = () => {
    if (!canRun) return;
    const next: CsamAuditQueryParams = {
      dimension,
      value: needsValue ? value.trim() : null,
      from: fromDate || null,
      to: toDate || null,
    };
    const unchanged = applied !== null && JSON.stringify(applied) === JSON.stringify(next);
    if (unchanged) {
      query.refetch();
    } else {
      setApplied(next);
    }
  };

  const rows = query.data?.rows ?? [];
  const isRunning = query.isFetching;
  const failed = query.data?.status === 'FAILED';
  const truncated = Boolean(query.data?.nextToken);

  const emptyState = (message: string, icon?: typeof SpinnerGapIcon | typeof WarningCircleIcon) => (
    <Surface tone="inverse" padding="lg" radius="md">
      <Inline gap="sm" align="center" justify="center">
        {icon ? <Icon icon={icon} size="sm" weight="fill" /> : null}
        <Text variant="bodySmall">{message}</Text>
      </Inline>
    </Surface>
  );

  const body = (
    <Stack gap="lg">
      <AuditEventDetailModal row={selectedRow} onClose={() => setSelectedRow(null)} />

      {!embedded && (
        <Stack gap="sm">
          <Inline gap="sm" align="center">
            <Icon icon={ClockCounterClockwiseIcon} size="lg" weight="duotone" />
            <Text as="h1" variant="title" weight="bold">
              {t('csam.audit.title')}
            </Text>
          </Inline>
          <Text variant="bodySmall">{t('csam.audit.intro')}</Text>
        </Stack>
      )}

      <Surface tone="inverse" radius="md" className="overflow-hidden">
        <CsamSectionHeader title={t('csam.audit.generate')} />
        <div className="p-4">
          <Inline gap="md" align="end" wrap className="tp-csam-audit-filters">
            <div className="min-w-40">
              <Combobox
                label={t('csam.audit.scopeTo')}
                fill="filled"
                searchable={false}
                value={dimension}
                onValueChange={(next) => {
                  if (next === 'user' || next === 'case' || next === 'tenant') setDimension(next);
                }}
                options={DIMENSIONS.map((item) => ({ value: item.key, label: t(item.labelKey) }))}
              />
            </div>

            {needsValue && (
              <div className="min-w-40 flex-[1_1_14rem]">
                <Input
                  background="inverse"
                  fill="filled"
                  label={t('csam.audit.identifier')}
                  type="text"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleRun();
                  }}
                  placeholder={(() => {
                    const key = DIMENSIONS.find((d) => d.key === dimension)?.placeholderKey;
                    return key ? t(key) : '';
                  })()}
                />
              </div>
            )}

            <div className="min-w-40">
              <Input
                background="inverse"
                fill="filled"
                label={t('csam.audit.from')}
                type="date"
                value={fromDate}
                max={toDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="min-w-40">
              <Input
                background="inverse"
                fill="filled"
                label={t('csam.audit.to')}
                type="date"
                value={toDate}
                min={fromDate}
                max={toDateInput(today)}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <Button
              type="button"
              className="tp-csam-audit-run"
              style="solid"
              color="accent"
              background="inverse"
              leadingIcon={<MagnifyingGlassIcon />}
              onClick={handleRun}
              disabled={!canRun}
              isLoading={isRunning}
              loadingText={t('csam.audit.running')}
            >
              {t('csam.audit.runQuery')}
            </Button>
          </Inline>
        </div>
      </Surface>

      <Stack gap="sm">
        <Inline align="center" justify="between">
          <Text variant="label" weight="semibold">
            {t('csam.audit.events')}
          </Text>
          {applied !== null && !isRunning && !failed && (
            <Text variant="bodyTiny">
              {rows.length} {rows.length === 1 ? 'event' : 'events'}
              {truncated ? ' (first page — refine filters to narrow)' : ''}
            </Text>
          )}
        </Inline>

        {applied === null && emptyState('Choose a filter and run a query to view audit events.')}
        {applied !== null && isRunning && emptyState('Running audit query… this can take up to 30 seconds.', SpinnerGapIcon)}
        {applied !== null && !isRunning && query.isError &&
          emptyState(auditQueryErrorCopy(query.error), WarningCircleIcon)}
        {applied !== null && !isRunning && failed &&
          emptyState(query.data?.error || 'Audit query failed. Please try again.', WarningCircleIcon)}
        {applied !== null && !isRunning && !query.isError && !failed && rows.length === 0 &&
          emptyState('No audit events match this query.')}

        {applied !== null && !isRunning && !query.isError && !failed && rows.length > 0 && (
          <Surface tone="inverse" padding="none" radius="md">
            <div className="tp-csam-audit-table-wrap">
              <table className="tp-csam-audit-table">
                <thead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <th key={String(column.key)}>{t(column.labelKey)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.event_id || index} onClick={() => setSelectedRow(row)}>
                      {COLUMNS.map((column) => (
                        <td key={String(column.key)} title={cellValue(row, column.key, formatDateTime, t)}>
                          {cellValue(row, column.key, formatDateTime, t)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Surface>
        )}
      </Stack>
    </Stack>
  );

  if (embedded) {
    return body;
  }

  return (
    <Surface tone="elevated" padding="lg">
      {body}
    </Surface>
  );
};

export default CsamAuditLog;
